import { useState } from "react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { ChampSelection } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { NATURES, nomPersonne } from "@/lib/libelles";
import { api, type Compte, type ProfilDetaille } from "@/lib/api";

/**
 * Les comptes et ce qu'ils portent (ADR-0035) : leurs profils et leur nature.
 *
 * Un compte se crée en ligne de commande (`creer-utilisateur`) : l'écran règle
 * des droits, il ne délivre pas de mots de passe. Il ne recopie aucun garde-fou
 * non plus. Il propose tous les profils à tous les comptes ; la base refuse ce
 * qu'un compte externe ne peut pas porter, ou le retrait du dernier
 * Administrateur, et son message s'affiche tel quel sur le compte en cause.
 */
export function PageComptes() {
  const comptes = useChargement((signal) => api.comptes(signal), []);
  const profils = useChargement((signal) => api.profilsAdministres(signal), []);

  const erreur = comptes.erreur ?? profils.erreur;
  if (erreur)
    return (
      <Erreur
        message={erreur}
        onReessayer={() => {
          comptes.recharger();
          profils.recharger();
        }}
      />
    );
  if (!comptes.donnees || !profils.donnees) return <Chargement message="Chargement des comptes…" />;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Comptes</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Les profils que chaque compte porte, et s'il est interne ou externe. Les comptes se créent
          et se désactivent en ligne de commande.
        </p>
      </div>
      {comptes.donnees.length === 0 ? <Vide titre="Aucun compte" /> : null}
      {comptes.donnees.map((compte) => (
        <FicheCompte key={compte.id} initial={compte} profils={profils.donnees ?? []} />
      ))}
    </div>
  );
}

function FicheCompte({ initial, profils }: { initial: Compte; profils: ProfilDetaille[] }) {
  const [compte, setCompte] = useState(initial);
  const [choisi, setChoisi] = useState("");
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  // Un retrait se confirme : un clic égaré ôterait un droit sans prévenir.
  const [aRetirer, setARetirer] = useState<string | null>(null);

  const portes = new Set(compte.profils.map((p) => p.code));
  const proposables = profils.filter((p) => !portes.has(p.code));
  const autreNature = compte.nature === "INTERNE" ? "EXTERNE" : "INTERNE";

  async function agir(travail: () => Promise<Compte>) {
    setEnvoi(true);
    setRefus(null);
    try {
      setCompte(await travail());
      return true;
    } catch (cause) {
      setRefus(messageDe(cause));
      return false;
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <article>
      <Carte>
        <EnTeteCarte className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-encre">{nomPersonne(compte)}</h2>
          {/* L'état se lit en toutes lettres (ADR-0024). */}
          <span className="flex flex-wrap gap-1.5">
            <Etiquette ton="neutre">
              {NATURES[compte.nature]}
            </Etiquette>
            <Etiquette ton={compte.actif ? "actif" : "neutre"}>
              {compte.actif ? "Actif" : "Désactivé"}
            </Etiquette>
          </span>
        </EnTeteCarte>
        <CorpsCarte className="flex flex-col gap-3">
          {compte.profils.length === 0 ? (
            <p className="text-sm text-encre-douce">Aucun profil : ce compte ne peut rien faire.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {compte.profils.map((profil) => (
                <li key={profil.code} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-encre">{profil.libelle}</span>
                  {aRetirer === profil.code ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-encre">Retirer ce profil ?</span>
                      <Bouton
                        taille="compact"
                        variante="principal"
                        disabled={envoi}
                        aria-label={`Confirmer le retrait de ${profil.libelle}`}
                        onClick={async () => {
                          await agir(() => api.retirerProfil(compte.id, profil.code));
                          setARetirer(null);
                        }}
                      >
                        Confirmer le retrait
                      </Bouton>
                      <Bouton
                        taille="compact"
                        variante="discret"
                        disabled={envoi}
                        onClick={() => setARetirer(null)}
                      >
                        Garder
                      </Bouton>
                    </span>
                  ) : (
                    <Bouton
                      taille="compact"
                      variante="discret"
                      disabled={envoi}
                      aria-label={`Retirer ${profil.libelle}`}
                      onClick={() => setARetirer(profil.code)}
                    >
                      Retirer
                    </Bouton>
                  )}
                </li>
              ))}
            </ul>
          )}

          {refus ? <Erreur message={refus} /> : null}

          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={async (evenement) => {
              evenement.preventDefault();
              if (await agir(() => api.attribuerProfil(compte.id, choisi))) setChoisi("");
            }}
          >
            <div className="min-w-0 flex-1 basis-56">
              <ChampSelection
                libelle="Profil à attribuer"
                options={[
                  { valeur: "", libelle: "— Choisir —" },
                  ...proposables.map((p) => ({ valeur: p.code, libelle: p.libelle })),
                ]}
                value={choisi}
                onChange={(e) => setChoisi(e.target.value)}
              />
            </div>
            <Bouton type="submit" disabled={envoi || !choisi}>
              Attribuer
            </Bouton>
          </form>

          <div>
            <Bouton
              taille="compact"
              variante="discret"
              disabled={envoi}
              onClick={() => void agir(() => api.changerNature(compte.id, autreNature))}
            >
              Passer en {NATURES[autreNature].toLowerCase()}
            </Bouton>
          </div>
        </CorpsCarte>
      </Carte>
    </article>
  );
}
